/**
 * @prettier
 */
import { useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';
import type { OrderVm } from 'src/api/pidedirecto/types/OrderVm';
import { SummaryRow } from 'src/components/SummaryRow';
import { OrderTypes } from 'src/constants/OrderType';
import { PaymentMethods } from 'src/constants/PaymentMethod';
import { PaymentStatuses } from 'src/constants/PaymentStatus';
import { RefundMethods } from 'src/constants/RefundMethod';
import { translate } from 'src/i18n/translate';
import type { CurrencyFormatVm } from 'src/types/CurrencyFormatVm';
import { formatAsNumberCurrency } from 'src/utils/number/formatAsNumberCurrency';
import { useSelector } from 'src/utils/react/useSelector';
import { negateNumberString } from 'src/utils/string/negateNumberString';
import { translatePaymentRejectReason } from 'src/utils/translate/translatePaymentRejectReason';

export function OrderSummaryOld({
    subtotal,
    productDiscount,
    promoCodeDiscount,
    customerDeliveryCost,
    driverTip,
    tax,
    taxRate,
    companyCredits,
    companyName,
    letsEatCredits,
    total,
    currencyFormat,
    order,
}: Props): React.ReactElement {
    const theme = useTheme();
    const classes = useStyles();

    const quickActionMessage = useSelector((state) => state.app.quickActionMessage);
    const markDriverTipAsServiceFeeEnabled = useSelector((state) => state.app.restaurant?.markDriverTipAsServiceFeeEnabled);

    return (
        <div className={classes.container}>
            {subtotal !== total && <SummaryRow text={translate('Subtotal')} value={formatAsNumberCurrency(subtotal, currencyFormat)} />}
            {productDiscount && <SummaryRow text={translate('Product Discount')} value={formatAsNumberCurrency(negateNumberString(productDiscount), currencyFormat)} />}
            {customerDeliveryCost && <SummaryRow text={translate('Delivery')} value={formatAsNumberCurrency(customerDeliveryCost, currencyFormat)} />}
            {driverTip && <SummaryRow text={markDriverTipAsServiceFeeEnabled ? translate('Service fee') : translate('Driver Tip')} value={formatAsNumberCurrency(driverTip, currencyFormat)} />}
            {tax && <SummaryRow text={translate('Tax')} value={formatAsNumberCurrency(tax, currencyFormat)} />}
            {promoCodeDiscount && <SummaryRow text={translate('Promo Code')} value={formatAsNumberCurrency(negateNumberString(promoCodeDiscount), currencyFormat)} />}
            {companyCredits && (
                <SummaryRow
                    text={translate('Paid by @company', { company: companyName })}
                    value={formatAsNumberCurrency(negateNumberString(companyCredits), currencyFormat)}
                    className={{ color: theme.palette.primary }}
                />
            )}
            {letsEatCredits && <SummaryRow text={translate('Credits')} value={formatAsNumberCurrency(negateNumberString(letsEatCredits), currencyFormat)} />}
            {order && <SummaryRow text={translatePaymentStatus(order)} />}
            {!!quickActionMessage && <p className={classes.quickActionMessage}>{quickActionMessage}</p>}
        </div>
    );
}

function translatePaymentStatus(order?: OrderVm): string {
    if (!order) {
        return '';
    }
    if (order.paymentMethod === PaymentMethods.CREDIT_CARD) {
        if (order.total === '0' && order.usedCredits) {
            if (order.paymentStatus === PaymentStatuses.REFUNDED) {
                return translate('Refunded credits');
            } else {
                return '';
            }
        }
        if (order.paymentStatus === PaymentStatuses.PAID) {
            return translate('Paid with card');
        }
        if (order.paymentStatus === PaymentStatuses.PAYED) {
            // TODO: Remove when deprecated and PAID is used in server instead
            return translate('Paid with card');
        }
        if (order.paymentStatus === PaymentStatuses.REFUNDED) {
            if (order.refundMethod === RefundMethods.CREDIT_CARD) {
                return translate('Paid with card, refunded to card');
            }
            if (order.refundMethod === RefundMethods.CREDITS) {
                return translate('Paid with card, refunded as credits');
            }
        }
        if (order.paymentStatus === PaymentStatuses.FAILED) {
            return translatePaymentRejectReason(order.paymentRejectReason);
        }
    } else if (order.paymentMethod === PaymentMethods.PAYROLL) {
        if (order.total === '0' && order.usedCredits) {
            if (order.paymentStatus === PaymentStatuses.REFUNDED) {
                return translate('Refunded credits');
            } else {
                return '';
            }
        }
        if (order.paymentStatus === PaymentStatuses.PAID) {
            return translate('Paid with payroll');
        }
        if (order.paymentStatus === PaymentStatuses.PAYED) {
            // TODO: Remove when deprecated and PAID is used in server instead
            return translate('Paid with payroll');
        }
        if (order.paymentStatus === PaymentStatuses.REFUNDED) {
            return translate('Paid with payroll, refunded');
        }
    } else if (order.paymentMethod === PaymentMethods.CASH) {
        if (order.paymentStatus === PaymentStatuses.UNPAID || !order.paymentStatus) {
            if (order.orderType === OrderTypes.PICKUP_STATION_ORDER || order.orderType === OrderTypes.DELIVERY_ORDER) {
                return translate('Paying at delivery');
            }
            return translate('Paying in store');
        }
        if (order.paymentStatus === PaymentStatuses.PAID) {
            if (order.orderType === OrderTypes.PICKUP_STATION_ORDER || order.orderType === OrderTypes.DELIVERY_ORDER) {
                return translate('Paid at delivery');
            }
            return translate('Paid in store');
        }
        if (order.paymentStatus === PaymentStatuses.PAYED) {
            // TODO: Remove when deprecated and PAID is used in server instead
            if (order.orderType === OrderTypes.PICKUP_STATION_ORDER || order.orderType === OrderTypes.DELIVERY_ORDER) {
                return translate('Paid at delivery');
            }
            return translate('Paid in store');
        }
    }
    return '';
}

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
    },
    quickActionMessage: {
        textAlign: 'left',
        fontWeight: 'bold',
        color: '#444444',
    },
}));

type Props = {
    subtotal: string;
    productDiscount?: string;
    promoCodeDiscount?: string;
    customerDeliveryCost?: string;
    driverTip?: string;
    tax?: string;
    taxRate?: string;
    companyCredits?: string;
    companyName?: string;
    letsEatCredits?: string;
    total: string;
    currencyFormat: CurrencyFormatVm;
    order?: OrderVm;
};
